import React, { useState, useEffect, useCallback } from 'react'
import { useMsal } from '@azure/msal-react'
import { createAuthApiService, getAuthApiService, type AuthUser } from '../services/authApi'

export interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

export interface AuthActions {
  login: () => Promise<void>
  logout: () => Promise<void>
  updatePreferences: (preferences: Partial<AuthUser['preferences']>) => Promise<void>
  clearError: () => void
  refreshUser: () => Promise<void>
}

/**
 * Custom hook for authentication state management
 * Integrates Azure AD authentication with backend JWT tokens
 */
export const useAuth = (): AuthState & AuthActions => {
  const { instance: msalInstance } = useMsal()
  
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  })

  // Initialize auth service
  useEffect(() => {
    createAuthApiService(msalInstance as any)
  }, [msalInstance])

  /**
   * Check authentication status on mount
   */
  useEffect(() => {
    checkAuthStatus()
  }, [])

  /**
   * Check if user is authenticated and load user data
   */
  const checkAuthStatus = useCallback(async () => {
    try {
      console.log('🔐 useAuth: checkAuthStatus called')
      setState(prev => ({ ...prev, isLoading: true, error: null }))

      const authService = getAuthApiService()

      // Check if we have a valid token
      if (!authService.isAuthenticated()) {
        console.log('🔐 useAuth: NOT authenticated')
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
        return
      }

      // Try to get current user profile
      console.log('🔐 useAuth: Fetching current user...')
      const user = await authService.getCurrentUser()
      console.log('🔐 useAuth: User fetched successfully:', user)

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      console.error('🔐 useAuth: Auth status check failed:', error)

      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Authentication check failed',
      })
    }
  }, [])

  /**
   * Login user with Azure AD
   */
  const login = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }))
      
      const authService = getAuthApiService()
      const authResponse = await authService.authenticateWithAzure()
      
      setState({
        user: authResponse.data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      console.error('Login failed:', error)
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }))
      
      throw error
    }
  }, [])

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }))
      
      const authService = getAuthApiService()
      await authService.logout()
      
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      console.error('Logout failed:', error)
      
      // Even if logout fails, clear the local state
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Logout failed',
      })
    }
  }, [])

  /**
   * Update user preferences
   */
  const updatePreferences = useCallback(async (preferences: Partial<AuthUser['preferences']>) => {
    try {
      setState(prev => ({ ...prev, error: null }))
      
      const authService = getAuthApiService()
      const updatedUser = await authService.updateUserPreferences(preferences)
      
      setState(prev => ({
        ...prev,
        user: updatedUser,
      }))
    } catch (error) {
      console.error('Update preferences failed:', error)
      
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update preferences',
      }))
      
      throw error
    }
  }, [])

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, error: null }))
      
      const authService = getAuthApiService()
      const user = await authService.getCurrentUser()
      
      setState(prev => ({
        ...prev,
        user,
      }))
    } catch (error) {
      console.error('Refresh user failed:', error)
      
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to refresh user data',
      }))
      
      throw error
    }
  }, [])

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  return {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    
    // Actions
    login,
    logout,
    updatePreferences,
    clearError,
    refreshUser,
  }
}

/**
 * Hook for checking if user has specific role
 */
export const useRole = (requiredRole: string): boolean => {
  const { user } = useAuth()
  
  if (!user) return false
  
  // Define role hierarchy
  const roleHierarchy = {
    'admin': ['admin', 'manager', 'user', 'viewer'],
    'manager': ['manager', 'user', 'viewer'],
    'user': ['user', 'viewer'],
    'viewer': ['viewer'],
  }
  
  const userRoles = roleHierarchy[user.role as keyof typeof roleHierarchy] || []
  return userRoles.includes(requiredRole)
}

/**
 * Hook for checking workspace access
 */
export const useWorkspaceAccess = (workspaceId?: string): boolean => {
  const { user } = useAuth()
  
  if (!user || !workspaceId) return false
  
  // Check if user has access to this workspace
  return user.workspaces.includes(workspaceId)
}

/**
 * Higher-order component for protecting routes
 */
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>,
  requiredRole?: string
) => {
  return (props: P) => {
    const { isAuthenticated, isLoading, user } = useAuth()
    
    if (isLoading) {
      return <div>Loading...</div> // Replace with your loading component
    }
    
    if (!isAuthenticated) {
      return <div>Please login to access this page</div> // Replace with login redirect
    }
    
    if (requiredRole && user && !useRole(requiredRole)) {
      return <div>Access denied. Insufficient permissions.</div> // Replace with access denied component
    }
    
    return <Component {...props} />
  }
}
